import { useCallback, useRef } from 'react';
import Header from './components/Header';
import HeroSection from './components/HeroSection';
import CategoryGrid from './components/CategoryGrid';
import SeoContentSection from './components/SeoContentSection';
import Footer from './components/Footer';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://kakobuyfinds.cam';

const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'Kakobuy Finds',
  url: siteUrl,
  logo: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/9f7209ca9e1d80750ccbea8c559f256f.png',
  description: 'The ultimate Kakobuy Spreadsheet resource hub with organized product finds, shopping guides, and comparison tools.',
  sameAs: [
    'https://kaspreadsheet.com',
  ],
};

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Kakobuy Finds',
  url: siteUrl,
  description: 'Discover the best Kakobuy Spreadsheet with thousands of organized finds, product lists, QC guides, price comparisons and shopping resources.',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${siteUrl}/?s={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

export default function Home() {
  const categoriesRef = useRef<HTMLDivElement>(null);

  const handleCategoryClick = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const scrollToCategories = useCallback(() => {
    categoriesRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={organizationSchema} />
      <JsonLd data={websiteSchema} />
      <Header onCategorySelect={handleCategoryClick} />

      <main>
        <HeroSection onMainCta={scrollToCategories} />

        <div ref={categoriesRef}>
          <CategoryGrid onCategoryClick={handleCategoryClick} />
        </div>

        <SeoContentSection />

        <section className="py-16 md:py-20 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6 text-center">
            <div className="relative rounded-2xl overflow-hidden">
              <img
                src="https://readdy.ai/api/search-image?query=Stylish%20minimalist%20desk%20scene%20featuring%20open%20laptop%20displaying%20vibrant%20fashion%20catalog%20grid%2C%20surrounded%20by%20carefully%20arranged%20accessories%20including%20leather%20watch%20straps%20bracelets%20and%20designer%20sunglasses%2C%20against%20soft%20pastel%20sage%20green%20painted%20wall%20background%2C%20gentle%20diffused%20studio%20lighting%20from%20large%20window%2C%20sophisticated%20clean%20composition%20with%20deliberate%20negative%20space%2C%20modern%20commercial%20product%20photography%2C%20calm%20aspirational%20lifestyle%20aesthetic&width=1600&height=700&seq=cta-section-kakobuy-renewed-v2&orientation=landscape"
                alt="Explore complete Kakobuy Spreadsheet product database"
                title="Kakobuy Spreadsheet - Complete Product Database"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/20 to-black/40"></div>
              <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-background-50 mb-4">
                  Ready to Discover Your Next Find?
                </h2>
                <p className="text-sm md:text-base text-background-50/80 max-w-md mb-8">
                  Browse the complete Kakobuy Spreadsheet with thousands of curated products across all categories.
                </p>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-8 py-3.5 rounded-full bg-primary-500 text-background-50 text-base font-semibold hover:bg-primary-600 transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}